import { useQuery } from '@apollo/client'
import { useTheme } from '@emotion/react'
import {
  Assessment,
  CalendarMonth,
  FeedbackSharp,
  FormatListBulleted,
  Inbox,
  Insights,
  MailOutline,
  OpenInNewOutlined,
  Person,
  PhoneCallback,
  PowerSettingsNewSharp,
  SettingsSharp,
  WarningAmberSharp
} from '@mui/icons-material'
import {
  Badge,
  Box,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery
} from '@mui/material'
import { Theme } from '@mui/material/styles'
import moment from 'moment'
import { FC, ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import { Hermine, HouseIcon, InfoBulb, ListIcon } from '../assets/icons'
import client, { resetAbortController } from '../client'
import { BrandLogo, SideBar } from '../components/atoms'
import { MenuList, MobileMenu, NotificationBell, TopBar, UserCard } from '../components/molecules'
import { AiChatDrawer, AiChatLauncher } from '../components/organisms/AiChat'
import config from '../config'
import { UNASSIGNED_CASE_EMAILS_COUNT_QUERY } from '../graphql/queries'
import { useChatStore } from '../stores/chatStore'
import {
  clearUserData,
  getClient,
  getDisabledMenuItems,
  getExpiry,
  getSidebarOpen,
  getToken,
  getUid,
  setSidebarOpen,
  setTimer
} from '../utils/helper'
import { useProfile, useUser } from '../utils/hooks'
import TitleContext from './TitleContext'

interface CustomerHeadersInit extends Headers {
  'access-token': string
  uid: string
  client: string
}

interface ContextWrapperProps {
  children?: ReactNode
}

// CK - NOTE: 1px smaller as in SideBar component
const drawerWidth = 380
const drawerWidthClosed = 60
const topBarHeight = 160
const profileEnabled = import.meta.env.VITE_PROFILE_ENABLED === 'true'
const hermineSignInUrl = import.meta.env.VITE_HERMINE_SIGN_IN_URL

const blinkingText = {
  '@keyframes blinker': {
    '50%': {
      opacity: 0
    }
  },
  animation: 'blinker 1s linear infinite'
}

const ContextWrapper: FC<ContextWrapperProps> = ({ children }) => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { pathname, search } = useLocation()

  const [open, setOpen] = useState(() => getSidebarOpen() ?? true)
  useEffect(() => {
    setSidebarOpen(open)
  }, [open])

  const scrollableRef = useRef<HTMLDivElement>(null)
  const [scrollbarWidth, setScrollbarWidth] = useState(0)
  useEffect(() => {
    const el = scrollableRef.current
    if (!el) return
    const check = () => {
      if (!open && el.scrollHeight > el.clientHeight) {
        setScrollbarWidth(el.offsetWidth - el.clientWidth)
      } else {
        setScrollbarWidth(0)
      }
    }
    check()
    const ro = new ResizeObserver(check)
    ro.observe(el)
    return () => ro.disconnect()
  }, [open])

  const effectiveClosedWidth = drawerWidthClosed + scrollbarWidth

  const {
    values: { title, subTitle, extra }
  } = useContext(TitleContext)

  const {
    user: { name, role },
    isEditor,
    isAdmin,
    reportingEnabled
  } = useUser()

  const { me, refetch, setMe } = useProfile()

  // Push-Modus des KI-Chat-Drawers: Hauptinhalt, TopBar und SideBar weichen
  // dem geöffneten Drawer aus (Overlay-Modus lässt das Layout unangetastet).
  const chatOpen = useChatStore(state => state.isOpen)
  const chatLayout = useChatStore(state => state.layout)

  // Badge für den E-Mail-Eingang: Anzahl der Mails ohne verantwortlichen
  // Bearbeiter. Pull-only-Ansicht — ohne den Zähler bleiben Mails unbemerkt.
  const { data: unassignedCountData } = useQuery(UNASSIGNED_CASE_EMAILS_COUNT_QUERY, {
    skip: !(isAdmin && me?.caseEmailsEnabled),
    pollInterval: 120000,
    fetchPolicy: 'cache-and-network'
  })
  const unassignedEmailsCount: number = unassignedCountData?.unassignedCaseEmailsCount ?? 0

  useEffect(() => {
    if (me && Object.keys(me).length === 0) {
      refetch()
    }
  }, [me, refetch])

  const logout = () => {
    fetch(`${config.apollo.baseUrl}/auth/sign_out`, {
      method: 'DELETE',
      headers: {
        'access-token': getToken(),
        uid: getUid(),
        client: getClient()
      } as CustomerHeadersInit
    }).then(res => {
      if (res.ok) {
        const expiry = getExpiry()
        setTimer(
          JSON.stringify({
            last_expiry: moment.unix(expiry ? Number(expiry) : 0).toLocaleString(),
            date: moment().toLocaleString(),
            message: 'logged out by User'
          })
        )
        setMe(null)
        clearUserData()
        resetAbortController('User logged out')
        client.clearStore()
        navigate('/')
      }
    })
  }

  // Check if user account is locked and logout immediately
  useEffect(() => {
    if (me?.lockedAt) {
      logout()
    }
  }, [me?.lockedAt])

  const theme = useTheme() as Theme
  const mobile = useMediaQuery(theme.breakpoints.down('md'))
  const landscape = useMediaQuery('(orientation: landscape)')

  const chatPush = !!me?.aiAvailable && chatOpen && chatLayout.mode === 'push' && !mobile
  const chatLeftOffset = chatPush && chatLayout.position === 'left' ? chatLayout.width : 0
  const chatRightOffset = chatPush && chatLayout.position === 'right' ? chatLayout.width : 0
  const chatBottomOffset = chatPush && chatLayout.position === 'bottom' ? chatLayout.height : 0

  const navIcon = (url: string | null | undefined, fallback: ReactNode): ReactNode =>
    url ? (
      <Box
        component="img"
        src={url}
        alt=""
        sx={{ width: '20px', height: '20px', objectFit: 'contain' }}
      />
    ) : (
      fallback
    )

  const menuItems = [
    {
      permissionId: 'dashboard',
      icon: <HouseIcon />,
      label: t('dashboard'),
      isNavItem: true,
      to: '/dashboard'
    },
    ...(reportingEnabled
      ? [
          {
            permissionId: 'report',
            icon: <Insights />,
            label: t('report'),
            isNavItem: true,
            to: '/report'
          }
        ]
      : []),
    ...(['user', 'legal_advisor'].includes(role)
      ? [
          {
            permissionId: 'tasks',
            icon: <FormatListBulleted />,
            label: t('tasks'),
            isNavItem: true,
            to: '/tasks'
          }
        ]
      : []),
    {
      permissionId: 'evaluation-rb',
      icon: <Assessment />,
      label: t('evaluationRB'),
      isNavItem: true,
      to: '/evaluation-rb'
    },
    ...(isAdmin || isEditor
      ? [
          {
            permissionId: 'einsatzplanung',
            icon: <CalendarMonth />,
            label: t('einsatzplanung'),
            isNavItem: true,
            to: '/einsatzplanung'
          }
        ]
      : []),
    {
      permissionId: 'cases',
      icon: <Person />,
      label: isAdmin ? t('casesOverview') : t('myCases'),
      isNavItem: true,
      to: '/cases',
      ...(!isEditor && { additionalHref: '/cases/new' })
    },
    {
      permissionId: 'cases-creator',
      icon: <ListIcon />,
      label: t('casesOverview'),
      isNavItem: true,
      to: '/cases-creator'
    },
    ...(role !== 'creator'
      ? (me?.pools || []).map((pool: any) => ({
          permissionId: pool.name,
          icon: navIcon(pool.icon?.url, <PhoneCallback />),
          label: pool.menuTitle,
          isNavItem: true,
          to: `/pools/${pool.slug}`
        })) || []
      : []),
    ...(isAdmin && me?.feedbacksEnabled
      ? [
          {
            permissionId: 'feedbacks',
            icon: <FeedbackSharp />,
            label: t('feedbacks'),
            isNavItem: true,
            to: '/feedbacks'
          }
        ]
      : []),
    ...(isAdmin && me?.payoutsVisible
      ? [
          {
            permissionId: 'payouts',
            icon: <InfoBulb />,
            label: t('payouts'),
            isNavItem: true,
            to: '/payouts'
          }
        ]
      : []),
    ...(isAdmin && me?.caseEmailsEnabled
      ? [
          {
            permissionId: 'unassigned-emails',
            icon: (
              <Badge badgeContent={unassignedEmailsCount} color="error" max={99}>
                <MailOutline style={{ height: '20px', width: '20px' }} />
              </Badge>
            ),
            label: t('unassignedEmails'),
            isNavItem: true,
            to: '/unassigned-emails'
          }
        ]
      : []),
    ...(['user', 'admin', 'legal_advisor'].includes(role) && me?.caseEmailsEnabled
      ? [
          {
            permissionId: 'my-emails',
            icon: <MailOutline />,
            label: t('myEmails'),
            isNavItem: true,
            to: '/my-emails'
          }
        ]
      : []),
    ...(isAdmin && me?.receivedEmailEnabled
      ? [
          {
            permissionId: 'received-emails',
            icon: <Inbox />,
            label: t('receivedEmails'),
            isNavItem: true,
            to: '/received-emails'
          }
        ]
      : []),
    // Removed since nobody used it. But will be required soon
    // ...(hermineSignInUrl && me?.aiAvailable
    //   ? [
    //       {
    //         permissionId: 'hermine',
    //         icon: <Hermine />,
    //         suffixIcon: <OpenInNewOutlined />,
    //         label: t('hermine'),
    //         isNavItem: false,
    //         externalLink: true,
    //         to: hermineSignInUrl
    //       }
    //     ]
    //   : []),
    ...(me?.aiAgents || [])
      .filter((aiAgent: any) => aiAgent.url)
      .map((aiAgent: any) => ({
        permissionId: `ki-${aiAgent.id}`,
        icon: navIcon(aiAgent.icon?.url, <Hermine />),
        suffixIcon: <OpenInNewOutlined />,
        label: aiAgent.name,
        isNavItem: false,
        externalLink: true,
        to: aiAgent.url
      }))
  ]

  return (
    <Box>
      {me?.away && mobile && (
        <Box
          sx={{
            position: 'fixed',
            ...blinkingText,
            zIndex: 999,
            width: '100%',
            color: '#ff0000',
            textAlign: 'center',
            padding: '20px',
            pointerEvents: 'none',
            fontSize: '1.5rem',
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <WarningAmberSharp
              sx={{
                height: '20px',
                width: '20px'
              }}
            />
            <Box
              component="span"
              sx={{
                paddingLeft: '10px',
                fontWeight: 400
              }}
            >
              {t('awayModeActive')}
            </Box>
          </Box>
        </Box>
      )}

      <TopBar
        title={title}
        subTitle={subTitle}
        extra={extra}
        position={mobile ? 'relative' : 'fixed'} // MF: "MUI doc: 'sticky' not IE11 compatible"
        zIndex={980}
        {...(mobile
          ? { marginLeft: '0px', width: '100%' }
          : {
              marginLeft: `${(open ? drawerWidth : effectiveClosedWidth) + chatLeftOffset}px`,
              width: `calc(100% - ${(open ? drawerWidth : effectiveClosedWidth) + chatLeftOffset + chatRightOffset}px)`
            })}
        height={`${topBarHeight}px`}
      />

      {!mobile ? (
        <SideBar
          open={open}
          setOpen={setOpen}
          drawerWidth={drawerWidth}
          drawerWidthClosed={effectiveClosedWidth}
          offsetLeft={chatLeftOffset}
          heightOffset={chatBottomOffset}
        >
          <Box
            sx={{
              width: open ? `${drawerWidth}px` : `${effectiveClosedWidth}px`,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden'
            }}
          >
            <Box ref={scrollableRef} sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
              <Box
                sx={{
                  display: 'flex',
                  padding: open ? '40px' : '40px 20px',
                  justifyContent: open ? 'start' : 'center'
                }}
              >
                <BrandLogo
                  width={open ? '300px' : undefined}
                  height={!open ? '73px' : undefined}
                  type={open ? 'default' : 'vertical'}
                />
              </Box>
              {me?.away && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    '@keyframes blinker': {
                      '50%': {
                        opacity: 0
                      }
                    },
                    animation: 'blinker 1s linear infinite'
                  }}
                >
                  <ListItemButton
                    component="span"
                    sx={{
                      paddingTop: '10px',
                      paddingBottom: '10px',
                      paddingLeft: open ? '37px' : '20px',
                      minHeight: '20px',
                      fontWeight: 400
                    }}
                    onClick={() => {
                      navigate(`/profile${search}`, { state: { from: pathname } })
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        paddingRight: '12px',
                        color: '#ff0000'
                      }}
                    >
                      <WarningAmberSharp sx={{ height: '20px', width: '20px' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('awayModeActive')}
                      disableTypography
                      sx={{
                        display: open ? '' : 'none',
                        color: '#ff0000',
                        fontWeight: 'typography.fontWeightBold',
                        margin: 0,
                        lineHeight: '20px',
                        fontSize: '1.5rem'
                      }}
                    />
                  </ListItemButton>
                </Box>
              )}

              <MenuList
                menuItems={menuItems}
                defaultActive={0}
                open={open}
                onChange={() => {}}
                disabled={getDisabledMenuItems(role)}
              />
            </Box>

            {/* Der obere Bereich verliert bei Scrollbar Breite an diese; hier gleichen wir
                das aus, damit die Icons unten auf derselben Achse sitzen wie die Menü-Icons. */}
            <Box sx={{ flexShrink: 0, paddingRight: open ? 0 : `${scrollbarWidth}px` }}>
              <NotificationBell sidebarMode open={open} />
              {(me || name) && (
                <UserCard
                  user={{
                    imageSrc: me?.avatar?.url || undefined,
                    name: me?.name || name,
                    role: (me && t(me?.role)) || t(role)
                  }}
                  logout={logout}
                  open={open}
                  {...(profileEnabled && {
                    onClick: () => navigate(`/profile${search}`, { state: { from: pathname } })
                  })}
                />
              )}
            </Box>
          </Box>
        </SideBar>
      ) : (
        <MobileMenu
          items={menuItems}
          landscape={landscape}
          disabled={getDisabledMenuItems(role)}
          actions={[
            {
              name: t('logout'),
              onClick: logout,
              icon: <PowerSettingsNewSharp />
            },
            ...(profileEnabled
              ? [
                  {
                    name: t('profile'),
                    onClick: () => navigate(`/profile${search}`, { state: { from: pathname } }),
                    icon: <SettingsSharp />
                  }
                ]
              : [])
          ]}
        />
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: 'fit-content',
          overflow: 'auto',
          marginLeft: `${(open ? drawerWidth : effectiveClosedWidth) + chatLeftOffset}px`,
          ...(mobile && { marginLeft: '0px' }),
          marginRight: `${chatRightOffset}px`,
          marginBottom: `${chatBottomOffset}px`,
          px: 4,
          pb: 4,
          pt: `${mobile ? 0 : topBarHeight + 32}px`, // MF: Offset for fixed topbar
          transition: (muiTheme: Theme) =>
            muiTheme.transitions.create(['margin-left', 'margin-right', 'margin-bottom'])
        }}
      >
        {children}
      </Box>

      {me?.aiAvailable && (
        <>
          <AiChatLauncher />
          <AiChatDrawer />
        </>
      )}
    </Box>
  )
}

export default ContextWrapper
